/**
 * Panel modal para la ficha del punto y la pregunta.
 *
 * Se implementa sobre <dialog> nativo, que resuelve de serie tres cosas que a
 * mano se hacen mal casi siempre: el foco queda atrapado dentro mientras esta
 * abierto, el resto de la pagina queda inerte para lectores de pantalla, y la
 * tecla Escape cierra.
 *
 * Aqui es donde encajaria un componente de dialogo de Tailwind Plus si se
 * prefiere su acabado; el comportamiento accesible ya esta cubierto por el
 * elemento nativo.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  abierto: boolean;
  titulo: string;
  alCerrar: () => void;
  children: ReactNode;
}

export function PanelDeslizante({ abierto, titulo, alCerrar, children }: Props) {
  const { t } = useTranslation();
  const refDialogo = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const dialogo = refDialogo.current;
    if (!dialogo) return;

    if (abierto && !dialogo.open) {
      dialogo.showModal();
    } else if (!abierto && dialogo.open) {
      dialogo.close();
    }
  }, [abierto]);

  return (
    <dialog
      ref={refDialogo}
      aria-labelledby="titulo-panel"
      onCancel={(evento) => {
        // Se intercepta para poder sincronizar el estado de React: sin esto,
        // cerrar con Escape dejaria el estado diciendo que sigue abierto.
        evento.preventDefault();
        alCerrar();
      }}
      onClick={(evento) => {
        // Clic fuera del contenido, sobre el fondo del dialogo.
        if (evento.target === refDialogo.current) alCerrar();
      }}
      className="m-0 max-h-[92dvh] w-full max-w-xl rounded-t-2xl bg-white p-0
                 backdrop:bg-tinta-900/50 sm:m-auto sm:rounded-2xl
                 mt-auto sm:mt-auto"
    >
      <div className="flex max-h-[92dvh] flex-col">
        {/*
          Asa de arrastre: no arrastra nada, es la señal aprendida de "esto es
          una hoja que sube desde abajo y se puede cerrar". Solo en movil, que
          es donde el panel llega pegado al borde inferior.
        */}
        <div aria-hidden="true" className="flex justify-center pt-2.5 sm:hidden">
          <span className="h-1 w-10 rounded-full bg-crema-200" />
        </div>

        <div className="flex items-start justify-between gap-4 border-b border-crema-200 p-5 pt-4">
          <h2 id="titulo-panel" className="titulo-ficha">
            {titulo}
          </h2>
          <button
            type="button"
            onClick={alCerrar}
            className="-m-2 flex size-11 shrink-0 items-center justify-center rounded-lg
                       text-tinta-500 hover:bg-crema-100 hover:text-tinta-900"
          >
            <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" className="size-5">
              <path
                d="m5 5 10 10M15 5 5 15"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
            <span className="sr-only">{t('app.tancar')}</span>
          </button>
        </div>

        <div className="overflow-y-auto p-5">{children}</div>
      </div>
    </dialog>
  );
}
